// Rules for selecting several time entries in My Logs and changing them together.
// Pure functions only, so they can be pinned by scripts/test-bulk-entries.mjs; the
// screen (LogsView in Dashboard.js) does the reading and writing.
//
// Why the lock rules live here and not only in the database: on production a
// closed month is enforced against NEW entries (a trigger on INSERT) but not
// against changes to existing ones, so the screen is the guard for edits and
// deletes. Selecting several entries makes a mistake cost several entries, so
// the rules are applied before anything is sent.

export const periodOf = (entry) => String(entry?.entry_date || '').slice(0, 7);

// A month is closed per person, per client, per month. `locks` are month_locks rows
// (a row with reopened_at set is a month that was closed and then reopened).
export function isClosed(userId, clientId, period, locks) {
  if (!clientId) return false; // entries with no client can't be closed
  return (locks || []).some(l => !l.reopened_at && l.user_id === userId && l.client_id === clientId && l.period === period);
}

export const isEntryLocked = (entry, locks, userId) => isClosed(userId, entry?.client_id, periodOf(entry), locks);

// What a person may tick: everything except entries in a closed month.
export const selectableEntries = (entries, locks, userId) => (entries || []).filter(e => !isEntryLocked(e, locks, userId));

// The entries that would end up in a closed (client, month) once `patch` is applied.
// `patch` holds only the fields being changed; a key that is absent leaves that field
// alone. Anything this returns blocks the whole change.
export function entriesLandingInClosedMonth(entries, patch, locks, userId) {
  const out = [];
  for (const e of entries || []) {
    const clientId = 'client_id' in patch ? patch.client_id : e.client_id;
    const date = 'entry_date' in patch ? patch.entry_date : e.entry_date;
    const period = String(date || '').slice(0, 7);
    if (isClosed(userId, clientId, period, locks)) out.push({ entry: e, clientId, period });
  }
  return out;
}

// Turns the form's "change this field" switches into the patch sent to the database.
// `fields` is { client|task|desc|billable|date: { on: boolean, value } }.
// A blank client or task type clears it (null); a blank description clears it too.
export function buildPatch(fields) {
  const f = fields || {};
  const patch = {};
  if (f.client?.on)   patch.client_id = f.client.value || null;
  if (f.task?.on)     patch.task_type_id = f.task.value || null;
  if (f.desc?.on)     patch.description = f.desc.value ?? '';
  if (f.billable?.on) patch.is_billable = !!f.billable.value;
  if (f.date?.on && f.date.value) patch.entry_date = f.date.value;
  return patch;
}

// Which tag links to remove and add so that every entry ends up right.
//   mode 'add'     every entry gets these tags, whatever else it has
//   mode 'remove'  these tags come off every entry that has them
//   mode 'replace' every entry ends with exactly these tags
// `entries[].time_entry_tags` is [{ tag_id }]. `tagIds` are real tag ids (new tags are
// created before this is called). Returns
//   removals: [{ tagId, entryIds }]   delete the link for each of these entries
//   inserts:  [{ time_entry_id, tag_id }]
// Only the difference is returned, so an entry that already has a tag is never
// asked to link it twice (the join table's key would refuse the whole insert).
export function planTagChanges(entries, mode, tagIds) {
  const want = [...new Set(tagIds || [])];
  const removalsByTag = new Map();
  const inserts = [];
  for (const e of entries || []) {
    const have = new Set((e.time_entry_tags || []).map(l => l.tag_id).filter(Boolean));
    let drop = [];
    let add = [];
    if (mode === 'add')          add = want.filter(t => !have.has(t));
    else if (mode === 'remove')  drop = want.filter(t => have.has(t));
    else if (mode === 'replace') { drop = [...have].filter(t => !want.includes(t)); add = want.filter(t => !have.has(t)); }
    for (const t of drop) {
      if (!removalsByTag.has(t)) removalsByTag.set(t, []);
      removalsByTag.get(t).push(e.id);
    }
    for (const t of add) inserts.push({ time_entry_id: e.id, tag_id: t });
  }
  return { removals: [...removalsByTag].map(([tagId, entryIds]) => ({ tagId, entryIds })), inserts };
}

export const hasTagChange = (plan) => !!plan && (plan.removals.length > 0 || plan.inserts.length > 0);

// Splits a list into pieces, so one request never carries an unbounded id list in its URL.
export function chunk(list, size) {
  const out = [];
  for (let i = 0; i < (list || []).length; i += size) out.push(list.slice(i, i + size));
  return out;
}
